// Copyright ©️ 2026 eVolve MEP, LLC
import type { ReactNode } from 'react';

import { formatDate, type DateOnly, type RenderableUser } from '@evolvemep/api-ts';
import { Avatar, Badge, Box, Button, Group } from '@mantine/core';

import { EvolveIcon } from 'assets/icons/EvolveIcon';
import { isNotNil } from 'helpers/isNotNil';
import { validateUserNameOrEmail } from 'helpers/userHelpers';

import { type EntityStatusBadgeProps, EntityStatusBadge } from './EntityStatusBadge';
import type { CustomStatusEntityName } from './StatusEditor/types';
import { UserAvatar } from './UserInfo';

/**
 * TODO: A better definition for this that does a little more lifing.
 */
export type MobileLinkButtonProps<T> = {
  /** The date associated with this link, displayed as a badge if provided. */
  date?: DateOnly | null | undefined;
  /** The users assigned to this link, displayed as avatars if provided. */
  assignees?: RenderableUser[];
  /** If provided, renders a {@link EntityStatusBadge} with the given props. */
  statusBadgeProps?: T extends CustomStatusEntityName ? EntityStatusBadgeProps<T> : undefined;
  /** The text label for the link button. */
  label?: ReactNode;
  /** The click handler for the link button. */
  onClick: () => void;
};

/** A style of button used to present a navigational link in a mobile interface. */
export const MobileLinkButton = <T = undefined,>({
  assignees,
  statusBadgeProps,
  label,
  onClick,
  ...rest
}: MobileLinkButtonProps<T>) => (
  <Button
    variant="light"
    justify="space-between"
    rightSection={<EvolveIcon icon="ArrowRight" size="xs" />}
    onClick={onClick}
    px="xs"
    size="md"
  >
    <Group gap="xs">
      {isNotNil(label) && <Box>{label}</Box>}
      {isNotNil(statusBadgeProps) && <EntityStatusBadge {...statusBadgeProps} />}
      {
        // We still want to display the date badge if a date is provided in the props but undefined
        // so the `in` operator is used instead of isNotNil here
        'date' in rest && (
          <Badge variant="light" size="lg" leftSection={<EvolveIcon icon="CalendarBoard" size="xs" />}>
            {formatDate(rest.date, 'MM/dd/yyyy')}
          </Badge>
        )
      }
      {isNotNil(assignees) && (
        <Avatar.Group>
          {assignees.map((assignee) => (
            <UserAvatar key={assignee.userId} name={validateUserNameOrEmail(assignee)} size="sm" />
          ))}
        </Avatar.Group>
      )}
    </Group>
  </Button>
);
